/* eslint-disable max-lines */
import React, { useMemo, useState } from 'react'
import ReactDOM from 'react-dom'
import { Drawer, Tooltip, Checkbox, Space, Button, message as $message } from 'antd'
import SettingOutlined from '@ant-design/icons/SettingOutlined'

import classNames from 'classnames'
import { sortableContainer, sortableElement, sortableHandle } from 'react-sortable-hoc'
import arrayMove from 'array-move'
import omit from 'lodash/omit'
import CustomIcon from '@/components/CustomIcon'
import Action from '@/components/Action'
import { visitTree } from '@/utils/tree'
import styles from './index.less'
import useColumnSetting from '../../hooks/useColumnSetting'
import { genColumnKey } from '../../utils'
import { getIsLogin } from '@/utils/token'

const LEFT = 'left'
const RIGHT = 'right'

const ResetBtn = Action(Button)
const SaveBtn = Action(Button)

const Drag = sortableHandle(({ className, ...restProps }) => {
    return (
        <Tooltip overlay={null} title="拖拽排序">
            <CustomIcon
                className={classNames(styles.action, className)}
                type="icon-a-Group1000008508"
                style={{ cursor: 'move' }}
                {...restProps}
            />
        </Tooltip>
    )
})

const getTitle = ({ label, title }) => {
    if (label) {
        return label
    }

    if (React.isValidElement(title)) {
        const { children } = title.props

        if (children) {
            const first = Array.isArray(children) ? children[0] : children

            if (typeof first === 'string') {
                return first
            }
        }
    }

    return title
}

const Item = ({ className, dataSource, fixed, checked, isGroup, onCheck, onFix, ...restProps }) => {
    const renderActions = () => {
        const fixedLeft = (
            <Tooltip overlay={null} title="固定在列首">
                <CustomIcon className={styles.action} type="icon-paixuTop" onClick={() => onFix(dataSource, LEFT)} />
            </Tooltip>
        )

        const noFixed = (
            <Tooltip overlay={null} title="不固定">
                <CustomIcon className={styles.action} type="icon-unpin" onClick={() => onFix(dataSource, '')} />
            </Tooltip>
        )

        const fixedRight = (
            <Tooltip overlay={null} title="固定在列尾">
                <CustomIcon className={styles.action} type="icon-paixuDown" onClick={() => onFix(dataSource, RIGHT)} />
            </Tooltip>
        )

        if (isGroup) {
            return null
        }

        if (fixed === LEFT) {
            return (
                <>
                    {noFixed}
                    {fixedRight}
                </>
            )
        }

        if (fixed === RIGHT) {
            return (
                <>
                    {fixedLeft}
                    {noFixed}
                </>
            )
        }

        return (
            <>
                {fixedLeft}
                {fixedRight}
            </>
        )
    }

    const title = getTitle(dataSource)

    return (
        <div className={classNames(styles.item, className)} {...restProps}>
            <Checkbox
                className={styles.title}
                checked={checked}
                disabled={dataSource.disabled}
                value={dataSource.key}
                onChange={({ target }) => onCheck(dataSource, target.checked)}
            >
                <Tooltip overlay={null} title={title}>
                    <span className={styles.columnTitle} style={{ userSelect: 'none' }}>
                        {title}
                    </span>
                </Tooltip>
            </Checkbox>
            {dataSource._disabled || dataSource._freezePosition ? null : (
                <Space className={styles.actions} size={12}>
                    {renderActions()}
                    <Drag />
                </Space>
            )}
        </div>
    )
}

const GroupItem = ({
    className,
    fixed,
    dataSource,
    checkChecked,
    onCheck,
    onGroupSort,
    onGroupCheck,
    onGroupFix,
    ...restProps
}) => {
    const renderActions = () => {
        const fixedLeft = (
            <Tooltip overlay={null} title="固定在列首">
                <CustomIcon
                    className={styles.action}
                    type="icon-paixuTop"
                    onClick={() => onGroupFix(dataSource, LEFT)}
                />
            </Tooltip>
        )

        const noFixed = (
            <Tooltip overlay={null} title="不固定">
                <CustomIcon className={styles.action} type="icon-unpin" onClick={() => onGroupFix(dataSource, '')} />
            </Tooltip>
        )

        const fixedRight = (
            <Tooltip overlay={null} title="固定在列尾">
                <CustomIcon
                    className={styles.action}
                    type="icon-paixuDown"
                    onClick={() => onGroupFix(dataSource, RIGHT)}
                />
            </Tooltip>
        )

        if (fixed === LEFT) {
            return (
                <>
                    {noFixed}
                    {fixedRight}
                </>
            )
        }

        if (fixed === RIGHT) {
            return (
                <>
                    {fixedLeft}
                    {noFixed}
                </>
            )
        }

        return (
            <>
                {fixedLeft}
                {fixedRight}
            </>
        )
    }

    const checkedProps = useMemo(() => {
        const checked = dataSource.children.length && dataSource.children.every(checkChecked)
        const indeterminate = checked ? false : dataSource.children.length && dataSource.children.some(checkChecked)

        return {
            checked,
            indeterminate,
        }
    }, [dataSource, checkChecked])

    const title = getTitle(dataSource)

    return (
        <div className={classNames(styles.groupItem, className)} {...restProps}>
            <div className={classNames(styles.item)}>
                <Checkbox
                    className={styles.title}
                    disabled={dataSource.disabled}
                    value={dataSource.key}
                    {...checkedProps}
                    onChange={({ target }) => onGroupCheck(dataSource, target.checked)}
                >
                    <Tooltip overlay={null} title={dataSource.title}>
                        <span className={styles.columnTitle} style={{ userSelect: 'none', fontWeight: 600 }}>
                            {title}
                        </span>
                    </Tooltip>
                </Checkbox>
                <Space className={styles.actions} size={12}>
                    {renderActions()}
                    <Drag />
                </Space>
            </div>

            <div className={styles.groupList}>
                <SortableContainer
                    helperClass={styles.sortHelper}
                    lockAxis="y"
                    useDragHandle
                    onSortEnd={(...args) => onGroupSort(dataSource, ...args)}
                >
                    {dataSource.children.map((item, index) => {
                        return (
                            <SortableElement
                                key={item.key}
                                index={index}
                                fixed={fixed}
                                isGroup={dataSource.children?.length}
                                dataSource={item}
                                disabled={item._disabled}
                                checked={checkChecked(item)}
                                onCheck={onCheck}
                            />
                        )
                    })}
                </SortableContainer>
            </div>
        </div>
    )
}

const Group = ({ children }) => {
    return <div className={styles.group}>{children}</div>
}

const SortableContainer = sortableContainer(Group)

const SortableElement = sortableElement(Item)

const SortableGroupElement = sortableElement(GroupItem)

const List = ({ title, list, fixed, onListChange }) => {
    const treeDataConfig = useMemo(() => {
        const checkedKeys = []

        const trans = arr => {
            return arr.map(v => {
                const columnKey = genColumnKey(v.key || (v.dataIndex || v.code)?.toString(), v.index)

                if (v._show !== false) {
                    checkedKeys.push(columnKey)
                }

                if (v.children?.length) {
                    return {
                        ...v,
                        key: columnKey,
                        children: trans(v.children),
                    }
                }

                return {
                    ...v,
                    key: columnKey,
                }
            })
        }

        const treeData = trans(list)

        return {
            treeData,
            checkedKeys,
        }
    }, [list])

    const handleCheck = (target, _show) => {
        const fn = arr => {
            return arr.map(v => {
                const obj = {
                    ...v,
                }

                if (v.key === target.key) {
                    Object.assign(obj, {
                        _show,
                    })
                }

                if (v.children?.length) {
                    Object.assign(obj, {
                        children: fn(v.children),
                    })
                }

                return obj
            })
        }

        const newList = fn(list)

        onListChange(fixed, newList)
    }

    const handleSort = ({ oldIndex, newIndex }) => {
        const newList = arrayMove(list, oldIndex, newIndex)

        onListChange(fixed, newList)
    }

    const handleFix = (target, _fixed) => {
        const newList = list.map(v => {
            if (v.key === target.key) {
                return {
                    ...v,
                    _fixed,
                }
            }

            return v
        })

        onListChange(fixed, newList)
    }

    const handleGroupCheck = (target, _show) => {
        const newList = list.map(v => {
            if (v.key === target.key) {
                return {
                    ...v,
                    children: v.children.map(v1 => {
                        return {
                            ...v1,
                            _show,
                        }
                    }),
                }
            }

            return v
        })

        onListChange(fixed, newList)
    }

    const handleGroupSort = (target, { oldIndex, newIndex }) => {
        const newChildren = arrayMove(target.children, oldIndex, newIndex)

        const newTreeData = treeDataConfig.treeData.map(v => {
            if (v.key === target.key) {
                return {
                    ...v,
                    children: newChildren,
                }
            }

            return v
        })

        const fn = arr => {
            return arr.map(v => {
                const target = list.find(v1 => v1.key === v.key)

                if (target) {
                    if (v.children?.length) {
                        return {
                            ...target,
                            children: fn(v.children),
                        }
                    }

                    return target
                }

                return v
            })
        }

        const newList = fn(newTreeData)

        onListChange(fixed, newList)
    }

    const handleGroupFix = (target, _fixed) => {
        const newList = list.map(v => {
            if (v.key === target.key) {
                return {
                    ...v,
                    _fixed,
                }
            }

            return v
        })

        onListChange(fixed, newList)
    }

    const checkChecked = target => {
        return treeDataConfig.checkedKeys.includes(target.key)
    }

    return (
        <div className={styles.list}>
            <div className={styles.listTitle}>{title}</div>

            <SortableContainer helperClass={styles.sortHelper} lockAxis="y" useDragHandle onSortEnd={handleSort}>
                {treeDataConfig.treeData.map((item, index) => {
                    if (item._freezePosition) {
                        return (
                            <Item
                                key={item.key}
                                index={index}
                                fixed={fixed}
                                dataSource={item}
                                disabled={item._disabled}
                                checked={checkChecked(item)}
                                onCheck={handleCheck}
                                onFix={handleFix}
                            />
                        )
                    }

                    if (item.children?.length) {
                        return (
                            <SortableGroupElement
                                key={item.key}
                                index={index}
                                fixed={fixed}
                                dataSource={item}
                                disabled={item._disabled}
                                checkChecked={checkChecked}
                                onCheck={handleCheck}
                                onGroupSort={handleGroupSort}
                                onGroupCheck={handleGroupCheck}
                                onGroupFix={handleGroupFix}
                            />
                        )
                    }

                    return (
                        <SortableElement
                            key={item.key}
                            index={index}
                            fixed={fixed}
                            dataSource={item}
                            disabled={item._disabled}
                            checked={checkChecked(item)}
                            onCheck={handleCheck}
                            onFix={handleFix}
                        />
                    )
                })}
            </SortableContainer>
        </div>
    )
}

const Content = ({
    insertFooterEl,
    columns,
    configKey,
    defaultConfig,
    resetConfig,
    config,
    excludeKeys,
    setConfig,
    onReset,
    onSave,
}) => {
    const [curConfig, setCurConfig] = useState(config)

    const { leftColumns, centerColumns, rightColumns } = useColumnSetting({
        columns,
        config: curConfig,
        defaultConfig,
        excludeKeys,
    })

    // filters 未快捷查询的字段
    const selectedKeys = Object.values(omit(curConfig || {}, 'filters')).filter(v => !v || v._show === false)

    const allColumns = [...leftColumns, ...centerColumns, ...rightColumns]

    const indeterminate = selectedKeys.length > 0 && selectedKeys.length !== allColumns.length

    const handleChange = ({ target: { checked } }) => {
        const newConfig = {}

        visitTree(allColumns, (c, k) => {
            newConfig[c.key] = {
                _sort: k,
                _fixed: c._fixed,
                _show: c._disabled ? c._show : checked,
                _disabled: c._disabled,
            }
        })

        setCurConfig(newConfig)
    }

    const handleListChange = (fixed, newList) => {
        let curColumns = []

        if (fixed === LEFT) {
            curColumns = [...newList, ...centerColumns, ...rightColumns]
        } else if (fixed === RIGHT) {
            curColumns = [...leftColumns, ...centerColumns, ...newList]
        } else {
            curColumns = [...leftColumns, ...newList, ...rightColumns]
        }

        const newConfig = {}

        visitTree(curColumns, (c, k) => {
            const target = (curConfig && curConfig[c.key]) || {}

            newConfig[c.key] = {
                ...target,
                _sort: k,
                _fixed: c._fixed,
                _show: c._show,
                _disabled: c._disabled,
            }
        })

        setCurConfig(newConfig)
    }

    // 校验提交数据
    const validateSubmitData = () => {
        const curConfigValuesArr = Object.values(curConfig || {})

        const notEmptyConfig = curConfigValuesArr.some(v => typeof v?._sort === 'number')

        if (notEmptyConfig) {
            const isAllHidden = curConfigValuesArr.every(v => !v._show)

            if (isAllHidden) {
                const msg = '请至少勾选一项'

                $message.error(msg)
                return Promise.reject(new Error(msg))
            }

            const isAllShowFixed = curConfigValuesArr.every(v => !v?._show || (v?._show && v?._fixed))

            if (isAllShowFixed) {
                const msg = '不支持所有列固定'

                $message.error(msg)
                return Promise.reject(new Error(msg))
            }
        }

        return Promise.resolve()
    }

    // 重置
    const handleReset = async () => {
        try {
            const newConfig = resetConfig || {}

            await setConfig(configKey, newConfig)

            $message.success('更新成功')
            setCurConfig(newConfig)
            onReset && onReset()
        } catch (e) {
            console.error('catchError', e)
        }
    }

    // 保存
    const handleSave = async () => {
        try {
            await validateSubmitData()

            await setConfig(configKey, curConfig)

            $message.success('更新成功')
            onReset && onSave()
        } catch (e) {
            console.error('catchError', e)
        }
    }

    const renderFooter = () => {
        const footerEl = insertFooterEl()

        if (!footerEl) {
            return
        }

        return ReactDOM.createPortal(
            <Space className={styles.footer}>
                <div>
                    <Checkbox
                        className={styles.allCheckAll}
                        indeterminate={indeterminate}
                        checked={selectedKeys.length === 0 && selectedKeys.length !== allColumns.length}
                        onChange={handleChange}
                    >
                        全选
                    </Checkbox>
                </div>
                <Space>
                    <ResetBtn type="ghost" onClick={handleReset}>
                        恢复默认
                    </ResetBtn>
                    <SaveBtn type="primary" onClick={handleSave}>
                        保存
                    </SaveBtn>
                </Space>
            </Space>,
            footerEl,
        )
    }

    return (
        <div className={styles.container}>
            <div className={styles.bd}>
                {leftColumns.length ? (
                    <List
                        title="固定在左侧"
                        list={leftColumns}
                        config={curConfig}
                        fixed={LEFT}
                        onListChange={handleListChange}
                    />
                ) : null}

                {centerColumns.length ? (
                    <List title="不固定" list={centerColumns} config={curConfig} onListChange={handleListChange} />
                ) : null}

                {rightColumns.length ? (
                    <List
                        title="固定在右侧"
                        list={rightColumns}
                        config={curConfig}
                        fixed={RIGHT}
                        onListChange={handleListChange}
                    />
                ) : null}
            </div>
            {renderFooter()}
        </div>
    )
}

const ColumnSetting = props => {
    const isLogin = getIsLogin()
    const [visible, setVisible] = useState(false)
    // eslint-disable-next-line
    const [_, setter] = useState({})

    const handleReset = () => {
        setVisible(false)
    }

    const handleSave = () => {
        setVisible(false)
    }

    const insertFooterEl = () => {
        return document.querySelector('#columnConfigDrawerFooter')
    }

    const forceUpdate = () => {
        setter({})
    }
    if (!isLogin) {
        return null
    }

    return (
        <>
            <Drawer
                className={styles.columnConfigWrapper}
                title="列表设置"
                maskStyle={{ background: 'transparent' }}
                visible={visible}
                width="310px"
                destroyOnClose
                footer={<div id="columnConfigDrawerFooter" />}
                onClose={() => {
                    setVisible(false)
                }}
            >
                <Content {...props} insertFooterEl={insertFooterEl} onReset={handleReset} onSave={handleSave} />
            </Drawer>
            <a
                onClick={() => {
                    setVisible(true)

                    setTimeout(() => {
                        forceUpdate()
                    }, 10)
                }}
                className={classNames(styles.setting, props.className)}
            >
                <SettingOutlined className={styles.icon} />
                列表设置
            </a>
        </>
    )
}

export default props => {
    return <ColumnSetting {...props} />
}
